import { useState } from "react";
import { FileText, CheckCircle, AlertTriangle, XCircle, Loader2, Sparkles, Server, Shield, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmailResultsButton } from "@/components/EmailResultsButton";

interface EmailHeaderAnalyzerProps {
  userEmail?: string | null;
  userName?: string | null;
  sessionToken?: string | null;
}

interface AuthResult {
  result: string;
  details: string;
}

interface RoutingHop {
  server: string;
  timestamp: string;
  action: string;
}

interface Issue {
  severity: "high" | "medium" | "low";
  issue: string;
  recommendation: string;
}

interface HeaderInfo {
  name: string;
  value: string;
  explanation: string;
}

interface AnalysisResult {
  summary: string;
  authentication: {
    spf: AuthResult;
    dkim: AuthResult;
    dmarc: AuthResult;
  };
  routing: RoutingHop[];
  sender: {
    from: string;
    replyTo?: string;
    returnPath?: string;
    ip?: string;
  };
  spamAnalysis: {
    score?: string;
    verdict: string;
    flags: string[];
  };
  issues: Issue[];
  rawHeaders: HeaderInfo[];
}

const sampleHeaders = `Delivered-To: recipient@example.com
Received: by 2002:a17:90a:1234:0:0:0:0 with SMTP id abc123;
        Wed, 15 Jan 2025 09:30:00 -0800 (PST)
Received: from mail-sender.example.com (mail-sender.example.com. [192.168.1.100])
        by mx.google.com with ESMTPS id xyz789
        for <recipient@example.com>;
        Wed, 15 Jan 2025 09:29:58 -0800 (PST)
Received-SPF: pass (google.com: domain of sender@example.com designates 192.168.1.100 as permitted sender)
Authentication-Results: mx.google.com;
       dkim=pass header.i=@example.com header.s=selector1;
       spf=pass (google.com: domain of sender@example.com designates 192.168.1.100 as permitted sender);
       dmarc=pass (p=REJECT sp=REJECT dis=NONE) header.from=example.com
DKIM-Signature: v=1; a=rsa-sha256; c=relaxed/relaxed; d=example.com; s=selector1;
From: Sender Name <sender@example.com>
To: recipient@example.com
Subject: Test Email
Date: Wed, 15 Jan 2025 09:29:55 -0800
Message-ID: <abc123@example.com>
X-Spam-Status: No, score=-0.1
X-Spam-Score: -0.1`;

export const EmailHeaderAnalyzer = ({ userEmail, userName, sessionToken }: EmailHeaderAnalyzerProps) => {
  const [headers, setHeaders] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const loadSample = () => {
    setHeaders(sampleHeaders);
    setResult(null);
  };

  const analyzeHeaders = async () => {
    if (!headers.trim()) {
      toast.error("Please paste email headers to analyze");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-email-headers", {
        body: { headers: headers.trim() },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResult(data);
    } catch (error) {
      console.error("Error analyzing headers:", error);
      toast.error("Failed to analyze headers. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getAuthIcon = (result: string) => {
    if (result === "pass") return <CheckCircle className="w-4 h-4 text-success" />;
    if (result === "fail") return <XCircle className="w-4 h-4 text-destructive" />;
    return <AlertTriangle className="w-4 h-4 text-warning" />;
  };

  const getAuthBg = (result: string) => {
    if (result === "pass") return "bg-success/10 border-success/20";
    if (result === "fail") return "bg-destructive/10 border-destructive/20";
    return "bg-warning/10 border-warning/20";
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high": return "bg-destructive/10 text-destructive border-destructive/20";
      case "medium": return "bg-warning/10 text-warning border-warning/20";
      case "low": return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <FileText className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Email Header Analyzer
          </h3>
          <p className="text-sm text-muted-foreground">
            Decode and debug email headers for deliverability issues
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              Raw Email Headers
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
          </div>
          <Textarea
            value={headers}
            onChange={(e) => {
              setHeaders(e.target.value);
              setResult(null);
            }}
            placeholder="Paste your email headers here (View Original in Gmail, or View Source in other clients)..."
            className="min-h-[150px] font-mono text-xs resize-none"
          />
        </div>

        <Button
          onClick={analyzeHeaders}
          disabled={isAnalyzing || !headers.trim()}
          className="w-full"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing Headers...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Analyze Headers
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Email Results Button */}
            <div className="flex justify-end">
              <EmailResultsButton
                toolId="header-analyzer"
                toolName="Email Header Analyzer"
                results={result}
                userEmail={userEmail || null}
                userName={userName || null}
                sessionToken={sessionToken || null}
              />
            </div>

            {/* Summary */}
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-foreground">{result.summary}</p>
            </div>

            {/* Authentication Results */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Authentication Results
              </h4>
              <div className="grid sm:grid-cols-3 gap-3">
                {["spf", "dkim", "dmarc"].map((auth) => {
                  const authData = result.authentication[auth as keyof typeof result.authentication];
                  return (
                    <div key={auth} className={`rounded-xl border p-3 ${getAuthBg(authData.result)}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {getAuthIcon(authData.result)}
                        <span className="font-semibold text-foreground uppercase text-sm">{auth}</span>
                        <span className={`ml-auto text-xs font-medium uppercase ${
                          authData.result === "pass" ? "text-success" :
                          authData.result === "fail" ? "text-destructive" : "text-warning"
                        }`}>
                          {authData.result}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{authData.details}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sender Info */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                Sender Information
              </h4>
              <div className="bg-muted/50 rounded-xl p-3 grid sm:grid-cols-2 gap-2">
                {result.sender.from && (
                  <div>
                    <p className="text-xs text-muted-foreground">From</p>
                    <p className="text-sm text-foreground font-medium">{result.sender.from}</p>
                  </div>
                )}
                {result.sender.returnPath && (
                  <div>
                    <p className="text-xs text-muted-foreground">Return-Path</p>
                    <p className="text-sm text-foreground">{result.sender.returnPath}</p>
                  </div>
                )}
                {result.sender.ip && (
                  <div>
                    <p className="text-xs text-muted-foreground">Sending IP</p>
                    <p className="text-sm text-foreground font-mono">{result.sender.ip}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Routing Path */}
            {result.routing.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Server className="w-4 h-4 text-primary" />
                  Email Routing Path
                </h4>
                <div className="space-y-2">
                  {result.routing.map((hop, i) => (
                    <div key={i} className="flex items-start gap-3 bg-muted/30 rounded-lg p-2.5">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{hop.server}</p>
                        <p className="text-xs text-muted-foreground">{hop.action}</p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{hop.timestamp}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues */}
            {result.issues.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Issues Found ({result.issues.length})
                </h4>
                <div className="space-y-2">
                  {result.issues.map((issue, i) => (
                    <div key={i} className={`rounded-xl border p-3 ${getSeverityColor(issue.severity)}`}>
                      <div className="flex items-start gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${getSeverityColor(issue.severity)}`}>
                          {issue.severity}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground">{issue.issue}</p>
                          <p className="text-xs text-muted-foreground mt-1">💡 {issue.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Spam Analysis */}
            {result.spamAnalysis && (
              <div className="bg-muted/50 rounded-xl p-3">
                <h4 className="text-sm font-medium text-foreground mb-2">Spam Analysis</h4>
                <div className="flex items-center gap-4 flex-wrap">
                  {result.spamAnalysis.score && (
                    <div>
                      <span className="text-xs text-muted-foreground">Score: </span>
                      <span className="text-sm font-medium text-foreground">{result.spamAnalysis.score}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-muted-foreground">Verdict: </span>
                    <span className={`text-sm font-medium ${
                      result.spamAnalysis.verdict === "ham" ? "text-success" :
                      result.spamAnalysis.verdict === "spam" ? "text-destructive" : "text-muted-foreground"
                    }`}>
                      {result.spamAnalysis.verdict === "ham" ? "Not Spam" : 
                       result.spamAnalysis.verdict === "spam" ? "Spam" : "Unknown"}
                    </span>
                  </div>
                </div>
                {result.spamAnalysis.flags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {result.spamAnalysis.flags.map((flag, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-background text-xs text-muted-foreground">
                        {flag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
